import { Row } from 'antd'
import CategorySegmentListItem from 'app/components/Core/CategorySegment/ListItem'
import { checkoutFooterPortalId } from 'app/components/Core/Checkout/Footer'
import useLineItemUpdate from 'app/components/Core/Checkout/hooks/useLineItemUpdate'
import { useProductCache } from 'app/components/Core/Checkout/hooks/useProduct'
import Form from 'app/components/Core/LineItem/Form'
import KInputText from 'app/components/Modules/Form/KInputText'
import KCol from 'app/components/UI-Elements/Layout/KCol'
import { normalizeCategorySegment } from 'app/lib/apollo/normalizers/CategorySegment'
import rentalNormalizer from 'app/lib/apollo/normalizers/Rental'
import { useFeedbacker } from 'app/lib/contexts/Feedbacker'
import { useCurrentCustomer } from 'app/lib/hooks/useCurrent'
import { useLazyQuery, useQuery } from 'app/lib/hooks/useQuery'
import { queries } from 'app/queries'
import * as rentalsCheckout from 'app/queries/Rentals/Checkout'
import * as React from 'react'
import { useEffect } from 'react'

interface Props {
  rentalId: string
  categorySegmentId: number
}

const Create: React.FC<Props> = ({ rentalId, categorySegmentId }) => {
  const updateProductCache = useProductCache(rentalId)
  const { earliest_rental_start_date } = useCurrentCustomer()
  const {
    data: { rental }
  } = useQuery<{
    rental: Partial<IRental>
  }>(rentalsCheckout.FETCH_RENTAL_BASE_INFO, {
    fetchPolicy: 'network-only',
    variables: { id: rentalId },
    normalizer: (data) => ({ rental: rentalNormalizer(data.rental) })
  })

  const {
    execute,
    loading,
    data: { categorySegment }
  } = useLazyQuery<{ categorySegment: ICategorySegment }>(queries.categorySegment.get, {
    fetchPolicy: 'no-cache',
    normalizer: (data) => ({ categorySegment: normalizeCategorySegment(data.categorySegment) })
  })

  const { open: openLineItemUpdateDrawer } = useLineItemUpdate()
  const { drawerfy } = useFeedbacker()
  const initialStartDate = getInitialStartDate()

  useEffect(() => {
    if (rental) {
      execute({
        variables: {
          zip: rental.deliveryAddress?.zip,
          id: categorySegmentId,
          includeCategoryAttributes: true,
          includeCategorySegmentDetails: true,
          includeMinimumReturnDate: true,
          startDate: initialStartDate
        }
      })
    }
  }, [rental])

  function openLineItemUpdate(lineItem: ILineItem) {
    if (categorySegment.hasAttachments) {
      if (lineItem.subItems.length) {
        openLineItemUpdateDrawer({ lineItemId: lineItem.id, initialPage: 'sub_items' }, true)
      } else {
        openLineItemUpdateDrawer({ lineItemId: lineItem.id, initialPage: 'packages' }, true)
      }
    } else {
      drawerfy.closeAll()
    }
  }

  function onSuccess(lineItem: ILineItem) {
    openLineItemUpdate(lineItem)
  }

  function getInitialStartDate() {
    if (rental?.hasLineItems) return rental.startDate

    return earliest_rental_start_date
  }

  return (
    <Row gutter={[0, 24]}>
      <KCol span={24} loading={!categorySegment}>
        <If condition={categorySegment}>
          <Row gutter={[0, 24]}>
            <KCol span={24}>
              <CategorySegmentListItem member={categorySegment} />
            </KCol>
            <KCol span={24} loading={loading}>
              <Form
                props={{
                  onSuccess,
                  portalId: checkoutFooterPortalId,
                  action: 'create',
                  mutationOptions: {
                    update: updateProductCache
                  }
                }}
                member={{
                  rentalId,
                  categorySegmentId,
                  categorySegment: categorySegment,
                  startDate: initialStartDate
                }}>
                <KInputText hidden name="rentalId" />
                <KInputText hidden name="categorySegmentId" />
              </Form>
            </KCol>
          </Row>
        </If>
      </KCol>
    </Row>
  )
}

export default Create
